import { useState } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import SeoHead from '../../components/feature/SeoHead';

type Tab = 'services' | 'cleanpass';

export default function EntreprisesPage() {
  const [activeTab, setActiveTab] = useState<Tab>('services');
  const [showCleanPassProModal, setShowCleanPassProModal] = useState(false);

  // --- Devis form ---
  const [devisData, setDevisData] = useState({
    entreprise: '', nom: '', prenom: '', email: '',
    telephone: '', nombreVehicules: '', typeService: '', message: ''
  });
  const [devisSubmitting, setDevisSubmitting] = useState(false);
  const [devisStatus, setDevisStatus] = useState<'idle' | 'success' | 'error'>('idle');

  // --- CleanPass Pro form ---
  const [cleanPassProFormData, setCleanPassProFormData] = useState({
    entreprise: '', nomContact: '', prenomContact: '',
    telephone: '', email: '', nombreVehicules: '',
    typeVehicules: '', formule: '', message: ''
  });
  const [isCleanPassProSubmitting, setIsCleanPassProSubmitting] = useState(false);
  const [cleanPassProSubmitStatus, setCleanPassProSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleDevisChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setDevisData(prev => ({ ...prev, [name]: value }));
  };

  const handleCleanPassProChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setCleanPassProFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleDevisSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setDevisSubmitting(true);
    setDevisStatus('idle');
    try {
      const body = new URLSearchParams();
      Object.entries(devisData).forEach(([k, v]) => body.append(k, v));
      const res = await fetch('https://readdy.ai/api/form/d5sb7g7c87p5i2kb2jf0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString()
      });
      if (res.ok) {
        try {
          await fetch(`${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/send-entreprise-notification`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}` },
            body: JSON.stringify(devisData),
          });
        } catch (_) { /* silent */ }
        setDevisStatus('success');
        setDevisData({ entreprise: '', nom: '', prenom: '', email: '', telephone: '', nombreVehicules: '', typeService: '', message: '' });
      } else {
        setDevisStatus('error');
      }
    } catch (_) {
      setDevisStatus('error');
    } finally {
      setDevisSubmitting(false);
    }
  };

  const handleCleanPassProSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsCleanPassProSubmitting(true);
    setCleanPassProSubmitStatus('idle');
    try {
      const body = new URLSearchParams();
      Object.entries(cleanPassProFormData).forEach(([k, v]) => body.append(k, v));
      const res = await fetch('https://readdy.ai/api/form/d6jo8d7rgrhbthj8n2cg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString()
      });
      if (res.ok) {
        try {
          await fetch(`${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/send-cleanpass-pro-notification`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}` },
            body: JSON.stringify(cleanPassProFormData),
          });
        } catch (_) { /* silent */ }
        setCleanPassProSubmitStatus('success');
        setCleanPassProFormData({ entreprise: '', nomContact: '', prenomContact: '', telephone: '', email: '', nombreVehicules: '', typeVehicules: '', formule: '', message: '' });
        setTimeout(() => { setShowCleanPassProModal(false); setCleanPassProSubmitStatus('idle'); }, 3000);
      } else {
        setCleanPassProSubmitStatus('error');
      }
    } catch (_) {
      setCleanPassProSubmitStatus('error');
    } finally {
      setIsCleanPassProSubmitting(false);
    }
  };

  const inputClass = "w-full px-4 py-3 bg-dark-card border border-dark-border rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-primary transition-colors text-sm";
  const labelClass = "block text-sm font-medium text-gray-300 mb-2";

  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://www.louatiautocenter.ch';

  return (
    <div className="min-h-screen bg-dark-bg">
      <SeoHead
        title="Services Entreprises & Flotte Automobile Payerne | Louati AUTOCENTER"
        description="Solutions professionnelles pour flottes automobiles à Payerne. Entretien, protection céramique, CleanPass Pro. Tarifs préférentiels pour entreprises. Devis gratuit."
        keywords="entretien flotte automobile Payerne, services entreprises auto Suisse, CleanPass Pro, nettoyage flotte véhicules"
        canonical={`${siteUrl}/entreprises`}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "WebPage",
          "name": "Services Entreprises & Flotte Automobile Payerne | Louati AUTOCENTER",
          "description": "Solutions professionnelles pour flottes automobiles à Payerne. Entretien, protection céramique, CleanPass Pro.",
          "url": `${siteUrl}/entreprises`,
          "inLanguage": "fr-CH",
          "breadcrumb": {
            "@type": "BreadcrumbList",
            "itemListElement": [
              { "@type": "ListItem", "position": 1, "name": "Accueil", "item": `${siteUrl}/` },
              { "@type": "ListItem", "position": 2, "name": "Entreprises", "item": `${siteUrl}/entreprises` }
            ]
          }
        }}
      />
      <Navbar />

      {/* Hero */}
      <section className="relative pt-32 pb-24 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/180238e1974f9be9a17afc3a85f95581.jpg"
            alt="Services Entreprises"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/60 to-black/80"></div>
        </div>
        <div className="relative max-w-4xl mx-auto px-6 text-center">
          <div className="inline-flex items-center space-x-2 bg-primary/20 border border-primary/30 rounded-full px-5 py-2 mb-6">
            <i className="ri-building-line text-primary"></i>
            <span className="text-primary text-sm font-medium">Solutions Professionnelles</span>
          </div>
          <h1 className="text-5xl lg:text-6xl font-bold text-white mb-5">
            Services <span className="text-primary">Entreprises</span>
          </h1>
          <p className="text-lg text-gray-300 max-w-2xl mx-auto mb-10">
            Entretien, protection et gestion de votre flotte automobile — des solutions sur mesure pour les professionnels.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <button
              onClick={() => { setActiveTab('services'); document.getElementById('tabs-section')?.scrollIntoView({ behavior: 'smooth' }); }}
              className="px-8 py-4 bg-primary text-white rounded-lg font-semibold hover:bg-primary/90 transition-all whitespace-nowrap cursor-pointer"
            >
              Demander un devis
            </button>
            <button
              onClick={() => { setActiveTab('cleanpass'); document.getElementById('tabs-section')?.scrollIntoView({ behavior: 'smooth' }); }}
              className="px-8 py-4 bg-white/10 text-white border border-white/20 rounded-lg font-semibold hover:bg-white/20 transition-all whitespace-nowrap cursor-pointer"
            >
              <i className="ri-vip-crown-line mr-2"></i>CleanPass Pro
            </button>
          </div>
        </div>
      </section>

      {/* Chiffres clés */}
      <section className="py-10 bg-dark-card border-y border-dark-border">
        <div className="max-w-5xl mx-auto px-6 grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
          {[
            { value: '6 mois', label: 'Engagement minimum' },
            { value: '-15%', label: 'Réduction flotte 8+ véhicules' },
            { value: '3 formules', label: 'CleanPass Pro disponibles' },
            { value: '1 contact', label: 'Interlocuteur dédié' },
          ].map((stat, i) => (
            <div key={i}>
              <div className="text-3xl font-bold text-primary mb-1">{stat.value}</div>
              <div className="text-gray-400 text-sm">{stat.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Tabs */}
      <section id="tabs-section" className="py-16 bg-dark-bg">
        <div className="max-w-7xl mx-auto px-6">

          {/* Tab switcher */}
          <div className="flex justify-center mb-12">
            <div className="inline-flex bg-dark-card border border-dark-border rounded-full p-1">
              <button
                onClick={() => setActiveTab('services')}
                className={`px-8 py-3 rounded-full font-semibold text-sm transition-all whitespace-nowrap cursor-pointer ${activeTab === 'services' ? 'bg-primary text-white shadow-lg' : 'text-gray-400 hover:text-white'}`}
              >
                <i className="ri-car-wash-line mr-2"></i>Nos Services
              </button>
              <button
                onClick={() => setActiveTab('cleanpass')}
                className={`px-8 py-3 rounded-full font-semibold text-sm transition-all whitespace-nowrap cursor-pointer ${activeTab === 'cleanpass' ? 'bg-primary text-white shadow-lg' : 'text-gray-400 hover:text-white'}`}
              >
                <i className="ri-vip-crown-line mr-2"></i>CleanPass Pro
              </button>
            </div>
          </div>

          {/* ===== TAB SERVICES ===== */}
          {activeTab === 'services' && (
            <div className="space-y-16">

              {/* Services cards */}
              <div>
                <h2 className="text-3xl font-bold text-white text-center mb-10">
                  Nos <span className="text-primary">prestations</span>
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                  {[
                    { icon: 'ri-sparkling-2-line', title: 'Entretien de Flotte', desc: 'Nettoyage intérieur/extérieur, lavage régulier, désinfection et désodorisation.' },
                    { icon: 'ri-shield-check-line', title: 'Protection Premium', desc: 'Traitement céramique, polish, protection PPF et traitement cuir.' },
                    { icon: 'ri-palette-line', title: 'Personnalisation', desc: 'Ciel étoilé, covering, vitres teintées et accessoires sur mesure.' },
                    { icon: 'ri-file-chart-line', title: 'Gestion de Parc', desc: 'Suivi personnalisé, rapports d\'intervention et planification automatique.' },
                  ].map((s, i) => (
                    <div key={i} className="bg-dark-card border border-dark-border rounded-xl p-6 hover:border-primary/50 transition-all group">
                      <div className="w-12 h-12 flex items-center justify-center bg-primary/10 rounded-lg mb-4 group-hover:bg-primary/20 transition-colors">
                        <i className={`${s.icon} text-2xl text-primary`}></i>
                      </div>
                      <h3 className="text-white font-bold mb-2">{s.title}</h3>
                      <p className="text-gray-400 text-sm leading-relaxed">{s.desc}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Avantages */}
              <div className="bg-dark-card border border-dark-border rounded-2xl p-8">
                <h3 className="text-2xl font-bold text-white mb-6 text-center">Pourquoi nous choisir ?</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                  {[
                    { icon: 'ri-calendar-check-line', text: 'Interventions planifiées selon vos contraintes' },
                    { icon: 'ri-price-tag-3-line', text: 'Tarifs préférentiels selon le volume' },
                    { icon: 'ri-map-pin-line', text: 'Service directement dans vos locaux' },
                    { icon: 'ri-file-list-3-line', text: 'Une seule facture mensuelle pour toute la flotte' },
                    { icon: 'ri-award-line', text: 'Produits professionnels et techniciens certifiés' },
                    { icon: 'ri-customer-service-2-line', text: 'Un interlocuteur dédié pour toutes vos demandes' },
                  ].map((a, i) => (
                    <div key={i} className="flex items-center space-x-3">
                      <div className="w-9 h-9 flex items-center justify-center bg-primary/10 rounded-lg shrink-0">
                        <i className={`${a.icon} text-primary`}></i>
                      </div>
                      <span className="text-gray-300 text-sm">{a.text}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Secteurs */}
              <div>
                <h3 className="text-2xl font-bold text-white text-center mb-8">Secteurs d'activité</h3>
                <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-4">
                  {[
                    { icon: 'ri-taxi-line', name: 'VTC & Taxis' },
                    { icon: 'ri-car-line', name: 'Locations' },
                    { icon: 'ri-building-line', name: 'Concessionnaires' },
                    { icon: 'ri-hotel-line', name: 'Hôtellerie' },
                    { icon: 'ri-briefcase-line', name: 'Entreprises' },
                    { icon: 'ri-truck-line', name: 'Flottes' },
                    { icon: 'ri-government-line', name: 'Administrations' },
                    { icon: 'ri-store-line', name: 'Commerces' },
                  ].map((s, i) => (
                    <div key={i} className="bg-dark-card border border-dark-border rounded-xl p-4 text-center hover:border-primary/50 transition-all group cursor-pointer">
                      <div className="w-10 h-10 flex items-center justify-center bg-primary/10 rounded-lg mx-auto mb-2 group-hover:bg-primary/20 transition-colors">
                        <i className={`${s.icon} text-xl text-primary`}></i>
                      </div>
                      <p className="text-white text-xs font-medium">{s.name}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Formulaire devis */}
              <div id="contact-form" className="max-w-3xl mx-auto">
                <div className="text-center mb-8">
                  <h3 className="text-3xl font-bold text-white mb-3">Demandez votre <span className="text-primary">devis</span></h3>
                  <p className="text-gray-400">Remplissez ce formulaire, nous vous recontactons rapidement.</p>
                </div>
                <div className="bg-dark-card border border-dark-border rounded-2xl p-8">
                  <form id="contact-entreprises-form" data-readdy-form onSubmit={handleDevisSubmit} className="space-y-5">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                      <div>
                        <label className={labelClass}>Nom de l'entreprise *</label>
                        <input type="text" name="entreprise" value={devisData.entreprise} onChange={handleDevisChange} required placeholder="Votre entreprise" className={inputClass} />
                      </div>
                      <div>
                        <label className={labelClass}>Nombre de véhicules *</label>
                        <select name="nombreVehicules" value={devisData.nombreVehicules} onChange={handleDevisChange} required className={`${inputClass} cursor-pointer`}>
                          <option value="">Sélectionnez</option>
                          <option value="1-3">1 à 3 véhicules</option>
                          <option value="4-7">4 à 7 véhicules</option>
                          <option value="8+">8 véhicules et plus</option>
                        </select>
                      </div>
                      <div>
                        <label className={labelClass}>Nom *</label>
                        <input type="text" name="nom" value={devisData.nom} onChange={handleDevisChange} required placeholder="Votre nom" className={inputClass} />
                      </div>
                      <div>
                        <label className={labelClass}>Prénom *</label>
                        <input type="text" name="prenom" value={devisData.prenom} onChange={handleDevisChange} required placeholder="Votre prénom" className={inputClass} />
                      </div>
                      <div>
                        <label className={labelClass}>Email *</label>
                        <input type="email" name="email" value={devisData.email} onChange={handleDevisChange} required placeholder="contact@entreprise.ch" className={inputClass} />
                      </div>
                      <div>
                        <label className={labelClass}>Téléphone *</label>
                        <input type="tel" name="telephone" value={devisData.telephone} onChange={handleDevisChange} required placeholder="+41 XX XXX XX XX" className={inputClass} />
                      </div>
                      <div className="md:col-span-2">
                        <label className={labelClass}>Service souhaité *</label>
                        <select name="typeService" value={devisData.typeService} onChange={handleDevisChange} required className={`${inputClass} cursor-pointer`}>
                          <option value="">Sélectionnez</option>
                          <option value="nettoyage-regulier">Nettoyage régulier</option>
                          <option value="detailing-complet">Detailing complet</option>
                          <option value="protection-ceramique">Protection céramique</option>
                          <option value="personnalisation">Personnalisation</option>
                          <option value="gestion-parc">Gestion de parc</option>
                          <option value="plusieurs">Plusieurs services</option>
                        </select>
                      </div>
                      <div className="md:col-span-2">
                        <label className={labelClass}>Message (optionnel)</label>
                        <textarea name="message" value={devisData.message} onChange={handleDevisChange} rows={3} maxLength={500} placeholder="Décrivez vos besoins..." className={`${inputClass} resize-none`}></textarea>
                        <p className="text-xs text-gray-500 mt-1">Maximum 500 caractères</p>
                      </div>
                    </div>
                    <button type="submit" disabled={devisSubmitting} className="w-full py-4 bg-primary text-white rounded-lg font-semibold hover:bg-primary/90 transition-all disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer flex items-center justify-center">
                      {devisSubmitting ? <><i className="ri-loader-4-line animate-spin mr-2"></i>Envoi...</> : <><i className="ri-send-plane-line mr-2"></i>Envoyer ma demande</>}
                    </button>
                    {devisStatus === 'success' && (
                      <div className="p-4 bg-green-500/10 border border-green-500/30 rounded-lg text-green-400 text-center flex items-center justify-center">
                        <i className="ri-check-line mr-2"></i>Demande envoyée ! Nous vous recontactons rapidement.
                      </div>
                    )}
                    {devisStatus === 'error' && (
                      <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-center flex items-center justify-center">
                        <i className="ri-error-warning-line mr-2"></i>Une erreur est survenue. Réessayez ou appelez-nous.
                      </div>
                    )}
                  </form>
                </div>
              </div>
            </div>
          )}

          {/* ===== TAB CLEANPASS PRO ===== */}
          {activeTab === 'cleanpass' && (
            <div className="space-y-14">

              {/* Intro */}
              <div className="text-center max-w-3xl mx-auto">
                <div className="inline-flex items-center space-x-2 bg-primary/20 border border-primary/30 rounded-full px-5 py-2 mb-5">
                  <i className="ri-vip-crown-line text-primary"></i>
                  <span className="text-primary text-sm font-medium">Programme d'Abonnement</span>
                </div>
                <h2 className="text-4xl font-bold text-white mb-5">
                  CleanPass Pro
                </h2>
                <p className="text-gray-400 leading-relaxed">
                  Un entretien régulier et planifié pour tous vos véhicules professionnels — utilitaires, véhicules de service ou voitures de fonction. Gagnez du temps, soignez votre image et offrez un environnement de conduite agréable à vos équipes.
                </p>
              </div>

              {/* Avantages */}
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                {[
                  { icon: 'ri-calendar-check-line', title: 'Propres toute l\'année' },
                  { icon: 'ri-time-line', title: 'Gain de temps' },
                  { icon: 'ri-star-line', title: 'Image professionnelle' },
                  { icon: 'ri-tools-line', title: 'Entretien planifié' },
                  { icon: 'ri-price-tag-3-line', title: 'Tarifs flotte' },
                ].map((a, i) => (
                  <div key={i} className="bg-dark-card border border-dark-border rounded-xl p-5 text-center hover:border-primary/50 transition-all group">
                    <div className="w-12 h-12 flex items-center justify-center bg-primary/10 rounded-lg mx-auto mb-3 group-hover:bg-primary/20 transition-colors">
                      <i className={`${a.icon} text-xl text-primary`}></i>
                    </div>
                    <p className="text-gray-300 text-xs font-medium leading-snug">{a.title}</p>
                  </div>
                ))}
              </div>

              {/* Formules */}
              <div>
                <h3 className="text-2xl font-bold text-white text-center mb-8">Choisissez votre formule</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {/* Cabine */}
                  <div className="bg-dark-card border border-dark-border rounded-2xl p-7 flex flex-col hover:border-primary/50 transition-all">
                    <div className="mb-5">
                      <p className="text-xs text-gray-500 uppercase tracking-widest mb-1">Utilitaires & Chantier</p>
                      <h4 className="text-xl font-bold text-white mb-3">Pro Cabine</h4>
                      <div className="flex items-baseline">
                        <span className="text-3xl font-bold text-primary">59 CHF</span>
                        <span className="text-gray-400 text-sm ml-2">/ véhicule / mois</span>
                      </div>
                    </div>
                    <ul className="space-y-2 mb-6 flex-1">
                      {['Aspiration cabine avant', 'Nettoyage des tapis', 'Nettoyage des plastiques', 'Nettoyage des vitres'].map((item, idx) => (
                        <li key={idx} className="flex items-center space-x-2 text-gray-400 text-sm">
                          <i className="ri-check-line text-primary shrink-0"></i>
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                    <button onClick={() => setShowCleanPassProModal(true)} className="w-full py-3 bg-primary/10 text-primary border border-primary/30 rounded-lg font-semibold hover:bg-primary hover:text-white transition-all whitespace-nowrap cursor-pointer">
                      Choisir cette formule
                    </button>
                  </div>

                  {/* Cabine + */}
                  <div className="bg-dark-card border border-dark-border rounded-2xl p-7 flex flex-col hover:border-primary/50 transition-all">
                    <div className="mb-5">
                      <p className="text-xs text-gray-500 uppercase tracking-widest mb-1">Utilitaires & Chantier</p>
                      <h4 className="text-xl font-bold text-white mb-3">Pro Cabine +</h4>
                      <div className="flex items-baseline">
                        <span className="text-3xl font-bold text-primary">79 CHF</span>
                        <span className="text-gray-400 text-sm ml-2">/ véhicule / mois</span>
                      </div>
                    </div>
                    <ul className="space-y-2 mb-6 flex-1">
                      {['Nettoyage cabine avant complet', 'Tapis et plastiques', 'Vitres', 'Nettoyage extérieur rapide'].map((item, idx) => (
                        <li key={idx} className="flex items-center space-x-2 text-gray-400 text-sm">
                          <i className="ri-check-line text-primary shrink-0"></i>
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                    <button onClick={() => setShowCleanPassProModal(true)} className="w-full py-3 bg-primary/10 text-primary border border-primary/30 rounded-lg font-semibold hover:bg-primary hover:text-white transition-all whitespace-nowrap cursor-pointer">
                      Choisir cette formule
                    </button>
                  </div>

                  {/* Fonction */}
                  <div className="bg-dark-card border-2 border-primary rounded-2xl p-7 flex flex-col relative">
                    <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                      <span className="bg-primary text-white text-xs font-bold px-4 py-1 rounded-full whitespace-nowrap">RECOMMANDÉ</span>
                    </div>
                    <div className="mb-5">
                      <p className="text-xs text-gray-500 uppercase tracking-widest mb-1">Véhicules de fonction</p>
                      <h4 className="text-xl font-bold text-white mb-3">Pro Fonction</h4>
                      <div className="flex items-baseline">
                        <span className="text-3xl font-bold text-primary">129 CHF</span>
                        <span className="text-gray-400 text-sm ml-2">/ véhicule / mois</span>
                      </div>
                    </div>
                    <ul className="space-y-2 mb-6 flex-1">
                      {['Nettoyage intérieur complet', 'Aspiration sièges et tapis', 'Nettoyage des plastiques', 'Nettoyage des vitres', 'Nettoyage extérieur express'].map((item, idx) => (
                        <li key={idx} className="flex items-center space-x-2 text-gray-400 text-sm">
                          <i className="ri-check-line text-primary shrink-0"></i>
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                    <button onClick={() => setShowCleanPassProModal(true)} className="w-full py-3 bg-primary text-white rounded-lg font-semibold hover:bg-primary/90 transition-all shadow-lg shadow-primary/20 whitespace-nowrap cursor-pointer">
                      Choisir cette formule
                    </button>
                  </div>
                </div>
              </div>

              {/* Réduction flotte + Conditions */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-gradient-to-br from-primary/10 to-primary/5 border border-primary/30 rounded-2xl p-7">
                  <h3 className="text-xl font-bold text-white mb-4 flex items-center">
                    <i className="ri-discount-percent-line text-primary mr-2"></i>Réduction Flotte
                  </h3>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between bg-dark-card border border-dark-border rounded-xl px-5 py-4">
                      <span className="text-gray-300 text-sm">4 à 7 véhicules</span>
                      <span className="text-2xl font-bold text-primary">-10%</span>
                    </div>
                    <div className="flex items-center justify-between bg-dark-card border border-dark-border rounded-xl px-5 py-4">
                      <span className="text-gray-300 text-sm">8 véhicules et plus</span>
                      <span className="text-2xl font-bold text-primary">-15%</span>
                    </div>
                  </div>
                </div>

                <div className="bg-dark-card border border-dark-border rounded-2xl p-7">
                  <h3 className="text-xl font-bold text-white mb-4 flex items-center">
                    <i className="ri-information-line text-primary mr-2"></i>Conditions
                  </h3>
                  <ul className="space-y-3">
                    {[
                      { icon: 'ri-calendar-line', text: 'Engagement minimum : 6 mois' },
                      { icon: 'ri-file-list-line', text: 'Facturation mensuelle possible' },
                      { icon: 'ri-calendar-check-line', text: 'Passages planifiés à l\'avance' },
                      { icon: 'ri-price-tag-line', text: 'Prix selon le type de véhicule' },
                    ].map((c, i) => (
                      <li key={i} className="flex items-start space-x-3">
                        <i className={`${c.icon} text-primary mt-0.5`}></i>
                        <span className="text-gray-400 text-sm">{c.text}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              {/* CTA */}
              <div className="text-center">
                <button
                  onClick={() => setShowCleanPassProModal(true)}
                  className="px-10 py-5 bg-primary text-white rounded-lg font-bold text-lg hover:bg-primary/90 transition-all shadow-xl shadow-primary/30 whitespace-nowrap cursor-pointer inline-flex items-center"
                >
                  <i className="ri-vip-crown-line mr-3 text-xl"></i>
                  Demander un abonnement CleanPass Pro
                </button>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Contact direct */}
      <section className="py-10 bg-dark-card border-t border-dark-border">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <p className="text-gray-400 mb-5 text-sm">Vous préférez nous contacter directement ?</p>
          <div className="flex flex-wrap justify-center gap-3">
            <a href="tel:+41796840588" className="inline-flex items-center px-5 py-3 bg-dark-bg border border-dark-border rounded-lg text-white hover:border-primary/50 transition-all text-sm whitespace-nowrap cursor-pointer">
              <i className="ri-phone-line text-primary mr-2"></i>+41 79 684 05 88
            </a>
            <a href="mailto:contact@louatiautocenter.ch" className="inline-flex items-center px-5 py-3 bg-dark-bg border border-dark-border rounded-lg text-white hover:border-primary/50 transition-all text-sm whitespace-nowrap cursor-pointer">
              <i className="ri-mail-line text-primary mr-2"></i>contact@louatiautocenter.ch
            </a>
            <a href="https://wa.me/41796840588" target="_blank" rel="noopener noreferrer" className="inline-flex items-center px-5 py-3 bg-dark-bg border border-dark-border rounded-lg text-white hover:border-primary/50 transition-all text-sm whitespace-nowrap cursor-pointer">
              <i className="ri-whatsapp-line text-green-500 mr-2"></i>WhatsApp
            </a>
          </div>
        </div>
      </section>

      {/* CleanPass Pro Modal */}
      {showCleanPassProModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-start justify-center p-4 overflow-y-auto">
          <div className="bg-dark-card border border-dark-border rounded-2xl w-full max-w-2xl my-8">
            <div className="sticky top-0 bg-dark-card border-b border-dark-border rounded-t-2xl p-6 flex items-center justify-between z-10">
              <div>
                <h3 className="text-xl font-bold text-white flex items-center">
                  <i className="ri-vip-crown-line text-primary mr-2"></i>Abonnement CleanPass Pro
                </h3>
                <p className="text-gray-400 text-sm mt-1">Remplissez ce formulaire pour recevoir votre offre personnalisée</p>
              </div>
              <button onClick={() => setShowCleanPassProModal(false)} className="w-9 h-9 flex items-center justify-center bg-dark-bg border border-dark-border rounded-lg text-gray-400 hover:text-white hover:border-primary/50 transition-all cursor-pointer">
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>

            <form id="cleanpass-pro-form" data-readdy-form onSubmit={handleCleanPassProSubmit} className="p-6 space-y-5">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div className="md:col-span-2">
                  <label className={labelClass}>Nom de l'entreprise *</label>
                  <input type="text" name="entreprise" value={cleanPassProFormData.entreprise} onChange={handleCleanPassProChange} required placeholder="Votre entreprise" className={`${inputClass} bg-dark-bg`} />
                </div>
                <div>
                  <label className={labelClass}>Nom du contact *</label>
                  <input type="text" name="nomContact" value={cleanPassProFormData.nomContact} onChange={handleCleanPassProChange} required placeholder="Nom" className={`${inputClass} bg-dark-bg`} />
                </div>
                <div>
                  <label className={labelClass}>Prénom *</label>
                  <input type="text" name="prenomContact" value={cleanPassProFormData.prenomContact} onChange={handleCleanPassProChange} required placeholder="Prénom" className={`${inputClass} bg-dark-bg`} />
                </div>
                <div>
                  <label className={labelClass}>Téléphone *</label>
                  <input type="tel" name="telephone" value={cleanPassProFormData.telephone} onChange={handleCleanPassProChange} required placeholder="+41 XX XXX XX XX" className={`${inputClass} bg-dark-bg`} />
                </div>
                <div>
                  <label className={labelClass}>Email *</label>
                  <input type="email" name="email" value={cleanPassProFormData.email} onChange={handleCleanPassProChange} required placeholder="contact@entreprise.ch" className={`${inputClass} bg-dark-bg`} />
                </div>
                <div>
                  <label className={labelClass}>Nombre de véhicules *</label>
                  <input type="text" name="nombreVehicules" value={cleanPassProFormData.nombreVehicules} onChange={handleCleanPassProChange} required placeholder="Ex: 5 véhicules" className={`${inputClass} bg-dark-bg`} />
                </div>
                <div>
                  <label className={labelClass}>Type de véhicules *</label>
                  <select name="typeVehicules" value={cleanPassProFormData.typeVehicules} onChange={handleCleanPassProChange} required className={`${inputClass} bg-dark-bg cursor-pointer`}>
                    <option value="">Sélectionnez</option>
                    <option value="chantier">Chantier / Utilitaire</option>
                    <option value="fonction">Fonction / Commercial</option>
                    <option value="mixte">Mixte</option>
                  </select>
                </div>
                <div className="md:col-span-2">
                  <label className={labelClass}>Formule souhaitée *</label>
                  <select name="formule" value={cleanPassProFormData.formule} onChange={handleCleanPassProChange} required className={`${inputClass} bg-dark-bg cursor-pointer`}>
                    <option value="">Sélectionnez</option>
                    <option value="cabine">Pro Cabine — 59 CHF/mois</option>
                    <option value="cabine-plus">Pro Cabine + — 79 CHF/mois</option>
                    <option value="fonction">Pro Fonction — 129 CHF/mois</option>
                    <option value="sur-mesure">Sur mesure</option>
                  </select>
                </div>
                <div className="md:col-span-2">
                  <label className={labelClass}>Message (optionnel)</label>
                  <textarea name="message" value={cleanPassProFormData.message} onChange={handleCleanPassProChange} rows={3} maxLength={500} placeholder="Décrivez vos besoins spécifiques..." className={`${inputClass} bg-dark-bg resize-none`}></textarea>
                  <p className="text-xs text-gray-500 mt-1">Maximum 500 caractères</p>
                </div>
              </div>

              <button type="submit" disabled={isCleanPassProSubmitting} className="w-full py-4 bg-primary text-white rounded-lg font-semibold hover:bg-primary/90 transition-all disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer flex items-center justify-center">
                {isCleanPassProSubmitting ? <><i className="ri-loader-4-line animate-spin mr-2"></i>Envoi...</> : <><i className="ri-send-plane-line mr-2"></i>Envoyer ma demande</>}
              </button>

              {cleanPassProSubmitStatus === 'success' && (
                <div className="p-4 bg-green-500/10 border border-green-500/30 rounded-lg text-green-400 text-center flex items-center justify-center">
                  <i className="ri-check-line mr-2"></i>Demande envoyée ! Nous vous recontactons rapidement.
                </div>
              )}
              {cleanPassProSubmitStatus === 'error' && (
                <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-center flex items-center justify-center">
                  <i className="ri-error-warning-line mr-2"></i>Une erreur est survenue. Réessayez ou appelez-nous.
                </div>
              )}
            </form>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}
